<template>
  <div class="flex">
    <div
      v-if="isMobile && isOpen"
      class="fixed inset-0 bg-black opacity-50 z-10"
      @click="showSideBar"
    ></div>

    <div
      :class="[
        isMobile && isOpen ? 'fixed z-20' : '',
        isOpen ? 'w-72' : 'w-16',
        isMobile && !isOpen ? 'hidden' : ''
      ]"
      class="inset-y-0 left-0 flex flex-col transition-all duration-300 transform bg-white border-r border-gray-200 min-h-[100vh]"
    >
      <div
        class="flex items-center px-4 flex-shrink-0"
        :class="isOpen ? 'h-20' : 'h-20 justify-center'"
      >
        <div
          class="w-full flex items-center"
          :class="isOpen ? 'justify-between' : 'justify-center'"
        >
          <Logo v-if="isOpen" width="120px" logo="variant" />

          <button @click="showSideBar" class="text-gray-500 focus:outline-none lg:block">
            <left-arrow v-if="isOpen" />
            <rigth-arrow v-else />
          </button>
        </div>
      </div>

      <nav class="mt-6 flex-1 overflow-y-auto" :class="isOpen ? 'px-4' : 'px-2'">
        <div
          v-bind:key="index"
          v-for="(menu, index) in filteredMenus"
          @click="clickItem($event, menu)"
          class="flex flex-col"
          :to="menu.path"
        >
          <div
            v-if="menu.active && isAuthorized(menu)"
            :class="[
              isMenuActive(menu) ? activeClass : inactiveClass,
              isOpen ? 'px-6' : 'justify-center'
            ]"
            class="flex items-center py-4 mt-1 duration-200 rounded cursor-pointer hover-scale"
          >
            <component
              :color="isMenuActive(menu) ? '#FFFFFF' : '#1E1E29'"
              :is="getIconComponent(menu.icon)"
            />
            <!-- Com selo/contador o nome encolhe (trunca) para a linha caber nos ~208 px. -->
            <span
              v-if="isOpen"
              :class="
                menu.badge || menuCounter(menu) !== null
                  ? 'ml-4 mr-2 min-w-0 truncate whitespace-nowrap'
                  : 'mx-4 whitespace-nowrap'
              "
              :title="menu.badge || menuCounter(menu) !== null ? menu.name : undefined"
              data-testid="sidebar-menu-name"
              >{{ menu.name }}</span
            >
            <LabelBadge
              v-if="isOpen && menu.badge"
              :label="menu.badge"
              :icon="null"
              color="blue"
              size="small"
              class="flex-none"
              data-testid="sidebar-badge-central-de-vagas"
            />
            <span
              v-if="isOpen && menuCounter(menu) !== null"
              data-testid="sidebar-counter-central-de-vagas"
              class="ml-auto flex-none rounded-full bg-[#FEF3E7] px-2 text-xs font-semibold text-[#B45309]"
              >{{ menuCounter(menu) }}</span
            >
            <v-tooltip v-if="!isOpen" activator="parent" location="right" :text="menu.name" />

            <div
              v-if="menu.childrens.length > 0 && isOpen"
              class="ml-auto"
              @click="clickExpand($event, menu.path)"
            >
              <a v-if="expandChildrens[menu.path]"
                ><up-icon :color="isMenuActive(menu) ? '#FFFFFF' : '#1E1E29'"
              /></a>
              <a v-else><down-icon :color="isMenuActive(menu) ? '#FFFFFF' : '#1E1E29'" /></a>
            </div>
          </div>
          <div v-if="menu.childrens.length > 0 && expandChildrens[menu.path] && isOpen">
            <template v-for="(children, childIndex) in menu.childrens" :key="childIndex">
              <div
                v-if="isAuthorized(children)"
                :class="[route.path === children.path ? activeClass : inactiveClass]"
                class="flex items-center px-6 py-2.5 duration-200 rounded cursor-pointer hover-scale"
                @click="clickChildrenItem($event, children)"
                :to="children.path"
              >
                <span class="mx-4">{{ children.name }}</span>
              </div>
            </template>
          </div>
        </div>
      </nav>
    </div>
  </div>
</template>

<script lang="ts">
import { defineAsyncComponent, Ref, ref, computed, watch } from 'vue'
import Logo from '@/components/designSystem/Logo/index.vue'
import ComputerIcon from '@/assets/icons/computer.vue'

import UpIcon from '@/assets/icons/up.vue'
import DownIcon from '@/assets/icons/down.vue'
import LeftArrow from '@/assets/icons/rightArrow.vue'
import RigthArrow from '@/assets/icons/leftArrow.vue'
import { useRouter, useRoute } from 'vue-router'
import { useSidebar } from '@/hooks/useSidebar'
import { useShellIsMobile } from '@/hooks/useShellIsMobile'
import sidebarItems from '../config/sidebar_items.json'

import { useFeatureFlags } from '@/hooks/useFeatureFlags'
import { useAuthStore } from '@/pinia-store/useAuthStore'
import { useRouteMetaUtils } from '@/router'
import { usePermissionsStore } from '@/pinia-store/usePermissionsStore'
import LabelBadge from '@/components/designSystem/Badges/LabelBadge.vue'
import { useVacancyCenterStore } from '@/modules/company/VacancyCenter/useVacancyCenterStore'

const activeClass = ref('bg-[#2F7F91] text-white font-semibold')
const inactiveClass = ref('')

interface MenuChildren {
  name: string
  path: string
}

interface MenuItem {
  active: boolean
  name: string
  path: string
  icon: string
  isAdmin?: boolean
  childrens: MenuChildren[]
  matchPrefixes?: string[]
  /** Selo ao lado do nome (ex.: "NOVO"). */
  badge?: string
  /** Contador ao lado do nome, lido de um store já carregado na sessão. */
  counter?: 'vacancyCenter'
}

export default {
  name: 'Sidebar',
  components: {
    Logo,
    ComputerIcon,
    UpIcon,
    DownIcon,
    LeftArrow,
    RigthArrow,
    LabelBadge
  },
  setup() {
    const { isOpen, setIsOpen } = useSidebar()
    const router = useRouter()
    const route = useRoute()
    const { getAuthorizationsByPath, hasMetaAuthorization } = useRouteMetaUtils()
    const authStore = useAuthStore()
    const permissionsStore = usePermissionsStore()
    const vacancyCenterStore = useVacancyCenterStore()

    const expandChildrens: Ref<Record<string, boolean>> = ref({})
    // Flag do celular ligada: corte de 768px (spec D5); desligada: < 800px como sempre.
    const isMobile = useShellIsMobile()

    const { flags } = useFeatureFlags()

    const allMenus = sidebarItems as { items: MenuItem[] }

    const filteredMenus = computed(() => {
      // eslint-disable-next-line complexity, sonarjs/cognitive-complexity
      return allMenus.items.filter((item) => {
        if (item.name === 'Políticas' && !flags.enabledPolicies) {
          return false
        }
        if (item.name === 'Aprovações' && !flags.enabledManagementExceptionalities) {
          return false
        }
        if (item.name === 'Relatórios' && !flags.enabledReports) {
          return false
        }
        if (item.name === 'Gestão' && !flags.enabledManagement) {
          return false
        }
        // Sem a flag a rota nem existe: `hasMetaAuthorization` não esconderia o item.
        if (item.name === 'Afiliados' && !flags.enabledAntecipeAffiliate) {
          return false
        }
        if (item.name === 'Demanda' && !flags.enabledDashboardDemand) {
          return false
        }
        if (item.name === 'Central de vagas' && !flags.enabledDashboardVacancyCenter) {
          return false
        }
        if (item.name === 'Avaliação Médica' && !flags.enabledMedicalEvaluation) {
          console.log('filtered out Avaliação Médica')
          return false
        }
        return true
      })
    })

    const menusWithChildrens = computed(() =>
      filteredMenus.value.filter((item: MenuItem) => item.childrens.length > 0)
    )

    /** `/unidades` e `/unidades/...`, mas não `/unidades-x`. */
    function isInsideSection(path: string, sectionPath: string): boolean {
      return path === sectionPath || path.startsWith(`${sectionPath}/`)
    }

    // Reage à navegação, não só ao mount: o Sidebar não remonta ao trocar de
    // rota, e a seção onde o usuário acabou de entrar precisa estar aberta.
    watch(
      () => route.path,
      (path) => {
        menusWithChildrens.value.forEach((item) => {
          expandChildrens.value[item.path] = isInsideSection(path, item.path)
        })
      },
      { immediate: true }
    )

    function getIconComponent(icon: string) {
      const component = defineAsyncComponent(() => import(`@/assets/icons/${icon}.vue`))
      return component
    }

    function isMenuActive(menu: MenuItem): boolean {
      if (route.path === menu.path) return true
      if (menu.matchPrefixes) {
        return menu.matchPrefixes.some((prefix) => route.path.startsWith(prefix))
      }
      return false
    }

    function clickExpand(e: Event, item: string) {
      e.stopPropagation()
      expandChildrens.value[item] = !expandChildrens.value[item]
    }

    function clickItem(e: Event, menu: MenuItem) {
      router.push(menu.path)

      if (isMobile.value) {
        setIsOpen(false)
      }
    }

    function clickChildrenItem(e: Event, children: MenuChildren) {
      e.stopPropagation()
      router.push(children.path)
    }

    /**
     * Contador do item: só aparece depois que a página da Central carregou na
     * sessão. O menu nunca dispara a requisição (evita um GET por navegação).
     */
    function menuCounter(menu: MenuItem): number | null {
      return menu.counter === 'vacancyCenter' ? vacancyCenterStore.menuCount : null
    }

    function showSideBar() {
      setIsOpen(!isOpen.value)
    }

    /** Vale para o item e para o filho: o que manda é o `meta.authorizations` da rota do path. */
    function isAuthorized(menu: { path: string }) {
      if (hasMetaAuthorization(menu.path)) {
        const authorizations = getAuthorizationsByPath(menu.path)

        if (authorizations && authorizations.length > 0) {
          const isUserAuthorized = authorizations.some((authorization) => {
            return permissionsStore.checkAnyRessources(authorization)
          })

          return isUserAuthorized
        }
      }

      //return false
      return true
    }

    return {
      isOpen,
      expandChildrens,
      isMobile,
      getIconComponent,
      clickExpand,
      clickItem,
      clickChildrenItem,
      showSideBar,
      filteredMenus,
      activeClass,
      inactiveClass,
      authStore,
      route,
      isAuthorized,
      isMenuActive,
      menuCounter
    }
  }
}
</script>

<style scoped>
.hover-scale:hover {
  background-color: rgba(47, 127, 145, 0.08);
}

.hidden {
  display: none;
}
</style>
